(() => {
  if (window.EXOBrandOrbit?.initialize) return;
  const TAU = Math.PI * 2;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const projects = {
    custom: {
      title: 'Engineer what the project needs.',
      copy: 'Start with the function, finish, quantity and deadline. We connect custom engineering to the processes and components that fit.',
      chain: 'Machining, fabrication, finishing and configurable hardware. A custom assembly can draw on several product systems at once.',
      brands: ['cnc', 'exo_hdw', 'exostrut'], route: 'exo-general'
    },
    short: {
      title: 'Make the quantity you actually need.',
      copy: 'A one-off, a prototype or a short run. Build the production approach around the job, with attention to the details that make the finished product feel right.',
      chain: 'Material sourcing, cut parts, sewn assemblies and final finishing. Existing hardware and webbing options give custom work a practical starting point.',
      brands: ['cnc', 'rolypulley', 'gameday'], route: 'exocnc'
    },
    launch: {
      title: 'Bring the product and the brand to market together.',
      copy: 'Connect a custom product line to its configurator, campaign and physical display. Carry the same design intent from the first sample to the customer experience.',
      chain: 'Printed textiles, Strapworks webbing, fabrication and assembly—connected to licensing coordination, content, commerce and launch strategy.',
      brands: ['gameday', 'virtus', 'os'], route: 'exo-general'
    },
    repeat: {
      title: 'Build once. Keep improving every run.',
      copy: 'Turn an approved design into a repeatable product system. Keep specifications, options and production information connected as quantities and customer needs change.',
      chain: 'Controlled components, manufacturing partners, testing and certification requirements, and coordinated releases. Product information stays connected through EXO OS.',
      brands: ['os', 'cnc', 'exo_hdw'], route: 'exo-os'
    }
  };
  function initializeBrandOrbit(section) {
    if (!section || section.dataset.orbitReady === 'true') return;
    const stage = section.querySelector('[data-orbit-stage]');
    const items = [...section.querySelectorAll('[data-orbit-item]')];
    if (!stage || items.length < 2) return;
    const previous = section.querySelector('[data-orbit-previous]');
    const next = section.querySelector('[data-orbit-next]');
    const status = section.querySelector('[data-orbit-status]');
    const picker = section.querySelector('[data-orbit-select]');
    const related = section.querySelector('[data-project-brands]');
    const needs = [...section.querySelectorAll('[data-orbit-need]')];
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const configuredVisible = parseInt(section.dataset.orbitVisibleCount || '5', 10);
    const orbitTilt = (parseFloat(section.dataset.orbitTilt || '0') || 0) * Math.PI / 180;
    const stepAngle = TAU / items.length;
    let rotation = Math.PI / 2;
    let targetRotation = rotation;
    let activeIndex = -1;
    let animationFrame = 0;
    let lastFrameTime = 0;
    let settling = false;
    let pointerId = null;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerLastX = 0;
    let dragging = false;
    let moved = false;
    let suppressClickUntil = 0;
    section.dataset.orbitReady = 'true';
    section.classList.add('is-ready');
    const centerLink = section.querySelector('.brand-orbit__center');
    if (centerLink) { centerLink.setAttribute('aria-hidden', 'true'); centerLink.tabIndex = -1; }
    const visibleCount = () => Math.min(items.length, Math.max(3, window.innerWidth < 1000 ? 3 : configuredVisible));
    const setActiveItem = index => {
      if (index === activeIndex) return;
      activeIndex = index;
      items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      if (picker) picker.value = String(index);
      related?.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.index) === index)));
    };
    const render = () => {
      const mobile = window.innerWidth < 640;
      const centerY = mobile ? 58 : 62;
      const stageWidth = stage.clientWidth;
      const horizontalRadius = stageWidth * (mobile ? 0.72 : 0.42);
      const verticalRadius = mobile ? 42 : 44;
      stage.style.setProperty("--orbit-center-y", `${centerY}px`);

      const geometry = items.map((item, index) => {
        const angle = rotation + index * stepAngle;
        const orbitX = Math.cos(angle) * horizontalRadius;
        const orbitY = Math.sin(angle) * verticalRadius;
        const left =
          stageWidth / 2 + orbitX * Math.cos(orbitTilt) - orbitY * Math.sin(orbitTilt);
        const top =
          centerY +
          orbitX * Math.sin(orbitTilt) +
          orbitY * Math.cos(orbitTilt);
        const depth = (Math.sin(angle) + 1) / 2;
        return { item, index, left, top, depth };
      });

      const visible = new Set(
        [...geometry]
          .sort((a, b) => b.depth - a.depth)
          .slice(0, visibleCount())
          .map(({ index }) => index),
      );
      const nearest = geometry.reduce((closest, candidate) =>
        candidate.depth > closest.depth ? candidate : closest,
      );
      const current = activeIndex >= 0 ? geometry[activeIndex] : null;

      if (
        !current ||
        (!settling && !dragging && nearest.index !== activeIndex && nearest.depth > current.depth + 0.025)
      ) {
        setActiveItem(nearest.index, activeIndex < 0);
      }

      geometry.forEach(({ item, index, left, top, depth }) => {
        const isVisible = visible.has(index);
        const isActive = index === activeIndex;
        const isLeaving = item.classList.contains("is-orbit-leaving");
        const isPresented = isActive || isLeaving;
        const scale = 0.68 + depth * 0.22;
        const opacity = isVisible ? 0.65 + depth * 0.35 : 0;
        const blur = 0;
        const perspectiveTilt = (1 - depth) * 8;
        const focusButton = item.querySelector("[data-orbit-focus]");
        const enterLink = item.querySelector("[data-orbit-enter]");

        item.style.setProperty("--orbit-left", `${left}px`);
        item.style.setProperty("--orbit-top", `${top}px`);
        item.style.setProperty("--orbit-scale", scale.toFixed(3));
        item.style.setProperty("--orbit-opacity", opacity.toFixed(3));
        item.style.setProperty("--orbit-blur", `${blur.toFixed(2)}px`);
        item.style.setProperty(
          "--orbit-perspective-tilt",
          `${perspectiveTilt.toFixed(2)}deg`,
        );
        item.style.setProperty(
          "--orbit-pointer",
          isVisible && !isLeaving ? "auto" : "none",
        );
        item.style.zIndex = String(Math.round(10 + depth * 70));
        item.setAttribute("aria-hidden", String(!isVisible && !isPresented));

        if (focusButton) {
          focusButton.tabIndex = isVisible && !isLeaving && !isActive ? 0 : -1;
          focusButton.setAttribute("aria-pressed", String(isActive));
        }
        if (enterLink) enterLink.tabIndex = isActive ? 0 : -1;
      });

      if (status && activeIndex >= 0) {
        const active = items[activeIndex];
        status.value = active.dataset.orbitName || "Brand";
        status.textContent = status.value;
      }
    };

    const schedule = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(animate);
    };
    const animate = timestamp => {
      animationFrame = 0;
      if (!section.isConnected) return;
      const delta = lastFrameTime ? clamp(timestamp - lastFrameTime, 0, 48) : 16;
      lastFrameTime = timestamp;
      if (settling) {
        const difference = targetRotation - rotation;
        rotation += difference * (1 - Math.pow(0.002, delta / 420));
        if (Math.abs(difference) < 0.0005 || reducedMotion?.matches) {
          rotation = targetRotation;
          settling = false;
        }
      }
      render();
      if (settling) schedule();
      else lastFrameTime = 0;
    };
    const settleOn = destination => {
      section.classList.add('has-interacted');
      const destinationIndex = ((Math.round((Math.PI / 2 - destination) / stepAngle) % items.length) + items.length) % items.length;
      const destinationImage = items[destinationIndex].querySelector('.brand-orbit__portal-image');
      if (destinationImage) destinationImage.loading = 'eager';
      targetRotation = destination;
      settling = true;
      schedule();
    };
    const focusItem = (index, immediate = false) => {
      if (index < 0 || index >= items.length) return;
      const destination = Math.PI / 2 - index * stepAngle;
      const target = destination + Math.round((rotation - destination) / TAU) * TAU;
      if (immediate) { rotation = targetRotation = target; settling = false; render(); }
      else settleOn(target);
    };
    const move = direction => settleOn((settling ? targetRotation : rotation) - direction * stepAngle);
    const chooseProject = (key, immediate = false) => {
      const project = projects[key];
      if (!project) return;
      needs.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.orbitNeed === key)));
      for (const [selector, value] of [['[data-project-title]',project.title],['[data-project-copy]',project.copy],['[data-project-chain]',project.chain]]) {
        const node = section.querySelector(selector); if (node) node.textContent = value;
      }
      const contact = section.querySelector('[data-project-contact]');
      if (contact) contact.href = '/pages/contact?route=' + project.route;
      if (related) {
        related.replaceChildren();
        project.brands.forEach(brand => {
          const index = items.findIndex(item => item.dataset.orbitBrand === brand);
          if (index < 0) return;
          const button = document.createElement('button');
          button.type = 'button'; button.textContent = items[index].dataset.orbitName + ' ↗';
          button.dataset.index = String(index);
          button.dataset.brand = brand;
          button.setAttribute('aria-pressed', String(index === activeIndex));
          button.setAttribute('aria-label', 'Explore ' + items[index].dataset.orbitName + ' in the viewer');
          button.addEventListener('click', () => focusItem(index));
          related.append(button);
        });
      }
      const lead = items.findIndex(item => item.dataset.orbitBrand === project.brands[0]);
      if (lead >= 0) focusItem(lead, immediate);
    };
    if (picker) {
      items.forEach((item, i) => { const option = document.createElement('option'); option.value = String(i); option.textContent = item.dataset.orbitName; picker.append(option); });
      picker.addEventListener('change', () => focusItem(Number(picker.value)));
    }
    needs.forEach(button => button.addEventListener('click', () => chooseProject(button.dataset.orbitNeed)));
    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    items.forEach((item, index) => item.querySelector('[data-orbit-focus]')?.addEventListener('click', event => {
      if (Date.now() < suppressClickUntil) { event.preventDefault(); return; }
      focusItem(index);
    }));
    stage.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1); }
      else if (event.key === 'Home') { event.preventDefault(); focusItem(0); }
      else if (event.key === 'End') { event.preventDefault(); focusItem(items.length - 1); }
    });
    stage.addEventListener('wheel', event => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) && !event.shiftKey) return;
      event.preventDefault();
      settleOn((settling ? targetRotation : rotation) - clamp((event.deltaX + event.deltaY) * 0.003, -stepAngle, stepAngle));
    }, {passive:false});
    stage.addEventListener('pointerdown', event => {
      if (event.button !== 0 || event.target.closest('a,select')) return;
      pointerId = event.pointerId; pointerStartX = pointerLastX = event.clientX; pointerStartY = event.clientY;
      dragging = true; moved = false; settling = false;
    });
    stage.addEventListener('pointermove', event => {
      if (!dragging || event.pointerId !== pointerId) return;
      const dx = event.clientX - pointerStartX, dy = event.clientY - pointerStartY;
      if (!moved) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { dragging = false; pointerId = null; return; }
        if (Math.abs(dx) < 6) return;
        moved = true; stage.setPointerCapture?.(pointerId); stage.classList.add('is-dragging');
      }
      rotation += (event.clientX - pointerLastX) / Math.max(stage.clientWidth, 1) * TAU * 1.15;
      pointerLastX = event.clientX; targetRotation = rotation; schedule();
    });
    const finishDrag = event => {
      if (!dragging || event.pointerId !== pointerId) return;
      dragging = false; stage.classList.remove('is-dragging');
      if (stage.hasPointerCapture?.(pointerId)) stage.releasePointerCapture(pointerId);
      if (moved) {
        suppressClickUntil = Date.now() + 260;
        const index = ((Math.round((Math.PI / 2 - rotation) / stepAngle) % items.length) + items.length) % items.length;
        focusItem(index);
      }
      pointerId = null;
    };
    stage.addEventListener('pointerup', finishDrag);
    stage.addEventListener('pointercancel', finishDrag);
    window.addEventListener('resize', schedule, {passive:true});
    chooseProject('custom', true);
    // No autoplay, timer, inertia, or requestAnimationFrame loop while at rest.
  }
  function initialize(root = document) {
    if (root.matches?.('[data-brand-orbit]')) initializeBrandOrbit(root);
    root.querySelectorAll?.('[data-brand-orbit]').forEach(initializeBrandOrbit);
  }
  window.EXOBrandOrbit = {initialize};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('exo:brand-orbit:load', event => initialize(event.detail?.root || document));
})();
